/**
 * 策略風險評分工具
 *
 * 計算 0～100 的風險分數（越高越危險）
 *
 * 權重：
 *   最大回撤   50%
 *   Sharpe     20%
 *   Sortino    15%
 *   Profit Factor 10%
 *   勝率        5%
 *
 * 風險等級：
 *   0～33  → 低風險
 *   34～66 → 中風險
 *   67～100 → 高風險
 *
 * ⚠️ 2026-09-21 虧損保底：回測總報酬 < 0 → 分數至少 67（高風險）。
 *   原因：這個分數本質上在量「坑挖多深」（回撤佔 50%），不是「會不會賺」。
 *   其餘四項（Sharpe/Sortino/PF/勝率）就算全部拉到最差，加總也只有 50 分，
 *   所以一支「每一筆都虧」的策略，只要回撤 < 16.5% 就會被標成「中風險」
 *   （實例：總報酬 -11.28%、勝率 0% → 61 分中風險）。
 *   但標題寫的是「策略風險評估」，使用者讀成「這策略安不安全」——
 *   而這個分數同時顯示在策略市集，使用者會拿它決定要不要付錢租別人的策略。
 *   另一層：佔 50% 的回撤是「已實現」口徑，本身就會低估 2~4 倍。
 *   → 虧錢的策略一律至少高風險，並由呼叫端用 isBacktestLoss() 另外標註原因。
 *   報酬率沒帶進來時不套用（維持原本行為），避免誤判。
 */

const clamp = (val) => Math.max(0, Math.min(100, val));

/** 虧損保底的分數（＝高風險區間的下限，與 getRiskLevel 的 67 對齊） */
const LOSS_FLOOR = 67;

/**
 * 回測總報酬是否為負。支援 camelCase（returnRatePct）與 snake_case（total_return_rate）。
 * 沒帶報酬率 → false（不套用保底，維持原本行為）。
 */
export function isBacktestLoss(kpi) {
  if (!kpi) return false;
  const v = kpi.returnRatePct ?? kpi.total_return_rate;
  if (v === undefined || v === null) return false;
  const n = Number(v);
  return Number.isFinite(n) && n < 0;
}

/**
 * 計算風險分數
 * @param {object} kpi - 支援 camelCase（maxDrawdownPct, sharpeRatio, ...）
 *                       或 snake_case（max_drawdown, sharpe_ratio, ...）
 * @returns {number|null} 0～100 整數，資料不足時回傳 null
 */
export function calcRiskScore(kpi) {
  if (!kpi) return null;

  const get = (camel, snake) => {
    const val = kpi[camel] ?? kpi[snake];
    return val !== undefined && val !== null ? Number(val) : null;
  };

  // 最大回撤 (50%) — 回撤 0% → 0分，50% 以上 → 100分
  const maxDrawdown = get("maxDrawdownPct", "max_drawdown");
  const drawdownScore = maxDrawdown !== null
    ? clamp(Math.abs(maxDrawdown) / 50 * 100)
    : 50;

  // Sharpe (20%) — ≥2 → 0分，≤0 → 100分
  const sharpe = get("sharpeRatio", "sharpe_ratio");
  const sharpeScore = sharpe !== null
    ? clamp((2 - sharpe) / 2 * 100)
    : 50;

  // Sortino (15%) — ≥2 → 0分，≤0 → 100分
  const sortino = get("sortinoRatio", "sortino_ratio");
  const sortinoScore = sortino !== null
    ? clamp((2 - sortino) / 2 * 100)
    : 50;

  // Profit Factor (10%) — ≥3 → 0分，≤1 → 100分
  const pf = get("profitFactor", "profit_factor");
  const pfScore = pf !== null
    ? clamp((3 - pf) / 2 * 100)
    : 50;

  // 勝率 (5%) — ≥60% → 0分，≤30% → 100分
  const winRate = get("winRate", "win_rate");
  const winRateScore = winRate !== null
    ? clamp((60 - winRate) / 30 * 100)
    : 50;

  const total =
    drawdownScore * 0.50 +
    sharpeScore   * 0.20 +
    sortinoScore  * 0.15 +
    pfScore       * 0.10 +
    winRateScore  * 0.05;

  const score = Math.round(total);
  // 虧損保底（見檔頭說明）：只往上拉、不往下壓，原本就 ≥67 的維持原分數
  return isBacktestLoss(kpi) ? Math.max(score, LOSS_FLOOR) : score;
}

// ════════════════════════════════════════════════════════════════════════════
//  方案 B（2026-09-21 試驗、2026-09-22 採用）：把「風險」與「表現」拆成兩個指標
// ════════════════════════════════════════════════════════════════════════════
//  上面的 calcRiskScore 把兩種意思混在一起：回撤(風險) 與 Sharpe/PF/勝率(表現)。
//  結果「每筆都虧、但虧得很慢」的策略會被標成中風險 —— 方案 A 用虧損保底止血，
//  但分數本身仍混了兩種意思。方案 B 讓每個分數只回答一個問題：
//    風險：虧損時可能有多痛？           → 只看回撤、連續虧損（與賺不賺無關）
//    表現：扣掉風險之後賺得好不好？     → 只看風險調整後報酬
//  兩者分開後，「低風險・虧損」就能被如實呈現：波動確實不大，但它是賠錢的。
//  2026-09-22 起 StrategyKPICharts 與 BeastResults 都改用方案 B；calcRiskScore（A 版）目前**沒有呼叫端**，
//  只保留供回退（回退時 StrategyKPICharts 的 LossTag/LossTip 語系 key 也還在）。

/**
 * 純風險分數 0～100（越高越危險），不含任何「賺不賺」的成分，也沒有虧損保底。
 *   最大回撤       70% —— 0% → 0 分、50% 以上 → 100 分（與舊公式同一把尺）
 *   最大連續虧損   30% —— 0 次 → 0 分、10 次以上 → 100 分（「心理上撐不撐得住」）
 * 缺值時該項給 50（不確定＝中間），與舊公式一致。
 */
export function calcRiskOnlyScore(kpi) {
  if (!kpi) return null;
  const get = (camel, snake) => {
    const v = kpi[camel] ?? kpi[snake];
    if (v === undefined || v === null) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const dd = get("maxDrawdownPct", "max_drawdown");
  const streak = get("maxConsecutiveLosses", "max_consecutive_losses");
  const ddScore = dd !== null ? clamp(Math.abs(dd) / 50 * 100) : 50;
  const streakScore = streak !== null ? clamp(streak / 10 * 100) : 50;
  return Math.round(ddScore * 0.7 + streakScore * 0.3);
}

/**
 * 表現評級（越高越好）。回傳 { grade, score }，score 只供內部與測試使用，畫面上只顯示 grade。
 *   總報酬 < 0 → 一律 "loss"（最差，且明確說出「虧損」，不讓分數去稀釋它）
 *   否則由三項組成 0～100：
 *     Sharpe         40% —— 0 → 0 分、2 以上 → 100 分
 *     獲利因子(PF)   35% —— 1 → 0 分、3 以上 → 100 分
 *     年化報酬(CAGR) 25% —— 0% → 0 分、20% 以上 → 100 分（用年化才能跨回測長度比較）
 *   勝率刻意不放：趨勢策略勝率常 30% 卻很賺，單看勝率會誤導；PF 已涵蓋贏輸比。
 *   分級：80+ 優 / 55+ 良 / 30+ 普通 / 其餘 差
 * 報酬率沒帶進來 → 回傳 null（無法判斷，不亂給）。
 */
export function calcPerformanceGrade(kpi) {
  if (!kpi) return null;
  const get = (camel, snake) => {
    const v = kpi[camel] ?? kpi[snake];
    if (v === undefined || v === null) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const ret = get("returnRatePct", "total_return_rate");
  if (ret === null) return null;
  if (ret < 0) return { grade: "loss", score: 0 };

  const sharpe = get("sharpeRatio", "sharpe_ratio");
  const pf = get("profitFactor", "profit_factor");
  const cagr = get("cagr", "cagr");
  const sharpeScore = sharpe !== null ? clamp(sharpe / 2 * 100) : 50;
  // PF 為 Infinity（從沒虧過）時 Number.isFinite 會擋掉 → 視為缺值給 50，不讓它灌滿分
  const pfScore = pf !== null ? clamp((pf - 1) / 2 * 100) : 50;
  const cagrScore = cagr !== null ? clamp(cagr / 20 * 100) : 50;
  const score = Math.round(sharpeScore * 0.4 + pfScore * 0.35 + cagrScore * 0.25);
  const grade = score >= 80 ? "excellent" : score >= 55 ? "good" : score >= 30 ? "fair" : "poor";
  return { grade, score };
}

/**
 * 依分數回傳風險等級資訊
 * @param {number|null} score
 * @returns {{ level: string, label: string, color: string }|null}
 */
export function getRiskLevel(score) {
  if (score === null || score === undefined) return null;
  if (score <= 33) return { level: "low",    label: "低風險", color: "success" };
  if (score <= 66) return { level: "medium", label: "中風險", color: "warning" };
  return               { level: "high",   label: "高風險", color: "error"   };
}
